import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const links = [
    { to: '/', label: 'Home', tid: NAV.home },
    { to: '/about', label: 'About', tid: NAV.about },
    { to: '/services', label: 'Services', tid: NAV.services },
    { to: '/industries', label: 'Industries', tid: NAV.industries },
    { to: '/work', label: 'Work', tid: NAV.work },
    { to: '/articles', label: 'Articles', tid: NAV.articles },
    { to: '/contact', label: 'Contact', tid: NAV.contact },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-300 ${
                scrolled
                    ? 'shadow-[0_6px_30px_-10px_rgba(31,27,77,0.18)] border-b border-brand-navy/10'
                    : 'border-b border-transparent'
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
                <Link to="/" data-testid={NAV.logo} className="flex items-center">
                    <img
                        src="/assets/cfl-logo.png"
                        alt="CFL Ad Group"
                        className="h-14 w-auto object-contain"
                    />
                    <span className="sr-only">CFL Ad Group — Direct Response Advertising</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === '/'}
                            data-testid={l.tid}
                            className={({ isActive }) =>
                                `px-4 py-2 text-sm font-semibold tracking-wide transition-colors relative ${
                                    isActive
                                        ? 'text-brand-navy'
                                        : 'text-brand-navy/60 hover:text-brand-navy'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && (
                                        <span className="absolute left-3 right-3 -bottom-1 h-[2px] bg-brand-orange" />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid={NAV.cta}
                        className="hidden md:inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-navy text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                    >
                        Schedule a Call
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <button
                        type="button"
                        data-testid={NAV.mobileToggle}
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-brand-navy/15 text-brand-navy hover:bg-brand-navy/5"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid={NAV.mobileMenu}
                    className="lg:hidden border-t border-brand-navy/10 bg-white"
                >
                    <div className="px-6 py-6 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === '/'}
                                data-testid={`${l.tid}-mobile`}
                                className={({ isActive }) =>
                                    `py-3 px-4 text-base font-semibold border-l-2 ${
                                        isActive
                                            ? 'text-brand-navy border-brand-orange bg-brand-navy/5'
                                            : 'text-brand-navy/70 border-transparent'
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid={`${NAV.cta}-mobile`}
                            className="mt-3 inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-5 py-4 text-xs font-bold uppercase tracking-[0.2em]"
                        >
                            Schedule a Call <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
